import './leasing-subject.scss';

import { useContext, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { CarInfoModal } from '@/components/Shared/entities';
import { Button, Input } from '@/components/Shared/ui';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import PlusIcon from '@/media/icons/plus.svg?react';
import TrashIcon from '@/media/icons/trash.svg?react';
import { formatPrice } from '@/services/formatter';

import { CreateTabContext } from '../../LlpOrder';
import type { CreateLeasingProps } from '../../widgets/create-tab';

type TProps = {
  incrementMaxFieldsCount: () => void;
  decrementMaxFieldsCount: () => void;
};

const LeasingSubject: React.FC<TProps> = ({
  incrementMaxFieldsCount,
  decrementMaxFieldsCount
}) => {
  const { isFieldsFrozen } = useContext(CreateTabContext);

  const {
    control,
    setValue,
    setError,
    formState: { errors }
  } = useFormContext<CreateLeasingProps>();

  const vehicles = useWatch({ control, name: 'vehicles' });
  const [infoModal, setInfoModal] = useState(-1);

  const handleAddNewVehicle = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setValue('vehicles', [
      ...vehicles,
      { info: undefined, price: '', count: '' }
    ]);
    incrementMaxFieldsCount();
  };

  const handleDeleteVehicle = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number
  ) => {
    e.preventDefault();
    setValue(
      'vehicles',
      vehicles.filter((_, i) => i !== index)
    );
    decrementMaxFieldsCount();
  };

  return (
    <div className="leasing-subject">
      {vehicles.map((_, index) => {
        return (
          <div className="leasing-subject__row" key={index}>
            <Controller
              control={control}
              name={`vehicles.${index}.info`}
              rules={{
                required: 'Пожалуйста, введите данные',
                validate: (value) => {
                  if (!value?.brand || !value?.model || !value?.year)
                    return 'Пожалуйста, введите данные';
                }
              }}
              render={({ field }) => {
                const info = vehicles[index].info;
                const value = [
                  info?.brand ? `${info.brand.name}, ` : '',
                  info?.model ? `${info.model.name}, ` : '',
                  info?.year ? `${info.year}` : ''
                ].join('');

                return (
                  <Input
                    {...field}
                    value={value}
                    label="Марка, модель и год выпуска"
                    isRequired
                    error={errors?.vehicles?.[index]?.info?.message}
                    endContent={
                      <span className="leasing-subject__arrow">
                        <ChevronDownIcon />
                      </span>
                    }
                    onChange={() => !isFieldsFrozen && setInfoModal(index)}
                    onClick={() => !isFieldsFrozen && setInfoModal(index)}
                    isFrozen={isFieldsFrozen}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name={`vehicles.${index}.price`}
              rules={{
                required: 'Пожалуйста, введите данные',
                min: { value: 1, message: 'Пожалуйста, введите данные' }
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="tel"
                  value={formatPrice(vehicles[index].price)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\s/g, '');
                    if (value.match(/\D/) || value.length > '10111222'.length)
                      return;
                    field.onChange(+value);
                  }}
                  label="Цена авто"
                  isRequired
                  error={errors?.vehicles?.[index]?.price?.message}
                  isFrozen={isFieldsFrozen}
                />
              )}
            />
            <Controller
              control={control}
              name={`vehicles.${index}.count`}
              rules={{
                required: 'Пожалуйста, введите данные',
                min: { value: 1, message: 'Пожалуйста, введите данные' }
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="tel"
                  value={formatPrice(vehicles[index].count)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\s/g, '');
                    if (value.match(/\D/)) return;
                    field.onChange(+value);
                  }}
                  label="Количество"
                  isRequired
                  error={errors?.vehicles?.[index]?.count?.message}
                  isFrozen={isFieldsFrozen}
                />
              )}
            />

            {vehicles.length > 1 && !isFieldsFrozen && (
              <button
                onClick={(e) => handleDeleteVehicle(e, index)}
                className="leasing-subject__delete"
              >
                <TrashIcon className="leasing-subject__delete-icon" />
              </button>
            )}
          </div>
        );
      })}

      {!isFieldsFrozen && (
        <Button
          type="button"
          onClick={handleAddNewVehicle}
          className="leasing-subject__add"
        >
          <>
            Добавить авто
            <PlusIcon />
          </>
        </Button>
      )}

      <CarInfoModal
        type="leasing"
        isOpened={infoModal !== -1}
        onClose={() => setInfoModal(-1)}
        keyToUse="uuid"
        onSave={(values) => {
          setValue(`vehicles.${infoModal}.info.brand`, {
            name: values.mark.name,
            uuid: values.mark.uuid!
          });
          setValue(`vehicles.${infoModal}.info.model`, {
            name: values.model.name,
            uuid: values.model.uuid!
          });
          setValue(`vehicles.${infoModal}.info.year`, values.year);
          setError(`vehicles.${infoModal}.info`, {});
          setInfoModal(-1);
        }}
        defaultValues={
          vehicles[infoModal]
            ? {
                mark: vehicles[infoModal].info?.brand?.name || '',
                model: vehicles[infoModal].info?.model?.name || '',
                year: vehicles[infoModal].info?.year || ''
              }
            : undefined
        }
      />
    </div>
  );
};

export default LeasingSubject;
